import type { ReactNode } from 'react';
import { cn } from '#client/lib/cn';

interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Shown instead of the label when `iconOnly`; the label becomes the a11y name. */
  icon?: ReactNode;
}

interface SegmentedProps<T extends string> {
  /** Names the group for screen readers. Required — a bare radiogroup is unusable. */
  label: string;
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Render icons alone, with the label exposed via aria-label and a tooltip. */
  iconOnly?: boolean | undefined;
  size?: 'sm' | 'md' | undefined;
  className?: string | undefined;
}

/**
 * A segmented control: two to four mutually exclusive options, all visible.
 *
 * Built as a `radiogroup` rather than a row of buttons, which is what earns
 * arrow-key navigation and correct state announcement from the platform
 * instead of hand-rolled key handling. Roving tabindex keeps the whole group a
 * single tab stop, so it doesn't add three stops to the topbar.
 *
 * Used for the theme toggle, the density control, and the dashboard's time
 * scope.
 */
export function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
  iconOnly = false,
  size = 'md',
  className,
}: SegmentedProps<T>) {
  const moveTo = (index: number) => {
    // Wrap, so the group is a loop like a native radio group.
    const next = options[(index + options.length) % options.length];
    if (next) onChange(next.value);
  };

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn('c-segmented', size === 'sm' && 'c-segmented--sm', className)}
    >
      {options.map((option, index) => {
        const selected = option.value === value;

        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={iconOnly ? option.label : undefined}
            title={iconOnly ? option.label : undefined}
            // Roving tabindex: only the selected option is in the tab order.
            tabIndex={selected ? 0 : -1}
            className={cn('c-segmented__option', selected && 'is-selected')}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                event.preventDefault();
                moveTo(index + 1);
              } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                event.preventDefault();
                moveTo(index - 1);
              }
            }}
          >
            {option.icon && (
              <span className="c-segmented__icon" aria-hidden="true">
                {option.icon}
              </span>
            )}
            {!iconOnly && <span>{option.label}</span>}
          </button>
        );
      })}
    </div>
  );
}
